import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground shadow-sm hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/80",
        outline: "text-foreground",
        success: "border-transparent bg-success text-success-foreground",
        warning: "border-transparent bg-warning text-warning-foreground",
        green: "border-transparent bg-success text-success-foreground",
        red: "border-transparent bg-destructive text-destructive-foreground",
        blue: "border-transparent bg-info text-info-foreground",
        orange: "border-transparent bg-warning text-warning-foreground",
        grey: "border-transparent bg-muted text-muted-foreground",
        purple: "border-transparent bg-primary text-primary-foreground",
      },
      size: {
        default: "px-2.5 py-0.5 text-xs",
        small: "px-2 py-0.5 text-[10px]",
        xsmall: "px-1.5 py-0 text-[10px]",
        base: "px-2.5 py-0.5 text-xs",
        large: "px-3 py-1 text-sm",
        xlarge: "px-4 py-1.5 text-base",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  color?: string
}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, size, color, ...props }, ref) => {
    // Map color prop to variant if provided
    const resolvedVariant = color && ["green", "red", "blue", "orange", "grey", "purple"].includes(color)
      ? color as BadgeProps["variant"]
      : variant;

    return (
      <div
        ref={ref}
        className={cn(badgeVariants({ variant: resolvedVariant, size }), className)}
        {...props}
      />
    )
  }
)

Badge.displayName = "Badge"

export { Badge, badgeVariants }
